import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Home.css";
import { Locale, translations } from "../i18n/translations";

// ── Platform detection ─────────────────────────────────────────
type Platform = "ios" | "android" | "windows" | "macos" | "unknown";

function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && !/Windows/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  if (/Windows/.test(ua)) return "windows";
  if (/Macintosh|Mac OS X/.test(ua)) return "macos";
  return "unknown";
}

const LANG_OPTIONS: { value: Locale; label: string; path: string }[] = [
  { value: "en", label: "English", path: "/" },
  { value: "zh", label: "中文",    path: "/zh" },
];

function LangSwitcher({ locale }: { locale: Locale }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANG_OPTIONS.find((o) => o.value === locale)!;

  return (
    <div className="hm-lang-switcher" ref={ref}>
      <button className="hm-lang-btn" onClick={() => setOpen((v) => !v)}>
        <span className="hm-lang-globe">🌐</span>
        <span>{current.label}</span>
        <span className={`hm-lang-arrow ${open ? "hm-lang-arrow-up" : ""}`}>▾</span>
      </button>
      {open && (
        <ul className="hm-lang-dropdown">
          {LANG_OPTIONS.map((opt) => (
            <li
              key={opt.value}
              className={`hm-lang-option ${opt.value === locale ? "hm-lang-option-active" : ""}`}
              onClick={() => {
                setOpen(false);
                navigate(opt.path);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Image asset paths ──────────────────────────────────────────
const LOGO_IMG           = "/new/logo.png";
const GLOBE_IMG          = "/new/globe.png";
const BLOB_SPEECH        = "/new/blob-speech.svg";
const BLOB_TEAL          = "/new/blob-teal.svg";
const BLOB_WHATSAPP      = "/new/blob-whatsapp.svg";
const BLOB_PERSON        = "/new/blob-person.svg";
const BLOB_CHAT          = "/new/blob-chat.svg";
const BLOB_BOOKMARK      = "/new/blob-bookmark.svg";
const PROFILE_WOMAN      = "/new/profile-woman.svg";
const PROFILE_MAN        = "/new/profile-man.svg";
const BLOB_TEAL_FEATURE  = "/new/blob-teal-feature.svg";
const BLOB_GROUPS        = "/new/blob-groups.svg";
const DL_APP_STORE       = "/new/download-app-store.svg";
const DL_IOS_CHN         = "/new/download-ios-chn.svg";
const DL_GOOGLE_PLAY     = "/new/download-google-play.svg";
const DL_MACOS           = "/new/download-macos.svg";
const DL_WINDOWS         = "/new/download-windows.svg";
const DL_ANDROID_CHN     = "/new/download-android-chn.svg";
const CHAT_S1            = "/new/chat-screen1.png";
const CHAT_S2            = "/new/chat-screen2.png";
const CHAT_S3            = "/new/chat-screen3.png";
const CHAT_S4            = "/new/chat-screen4.png";
const GROUPS_S1          = "/new/groups-screen1.png";
const GROUPS_S2          = "/new/groups-screen2.png";
const QUOT_CHATS         = "/new/quotation-chats.png";
const QUOT_DISCOVERY     = "/new/quotation-discovery.png";
const QUOT_GROUPS        = "/new/quotation-groups.png";
const QUOT_SECURITY      = "/new/quotation-security.png";
const SECURITY_IMG       = "/new/security-people.png";
const DISCOVERY_PHONE    = "/new/discovery-phone.svg";
const IPHONE_MOCKUP      = "/new/iphone-mockup.svg";
const BLOB_TEAL2         = "/new/blob-teal2.svg";
const BLOB_SECURITY      = "/new/blob-security.svg";
const DISCOVERY_BG       = "/new/discovery-bg.svg";
const ICON_APPLE         = "/new/icon-apple.png";
const ICON_GOOGLE_PLAY   = "/new/icon-google-play.png";
const ICON_MACOS         = "/new/icon-macos.png";
const ICON_WINDOWS       = "/new/icon-windows.png";
const ICON_ANDROID       = "/new/icon-android.png";

// ── Download URLs ───────────────────────────────────────────────
const DOWNLOAD_URLS = {
  appStore:    "https://apps.apple.com/ca/app/vennchat/id6443394582",
  iosChn:      "/chinese-apple-instructions",
  googlePlay:  "https://play.google.com/store/apps/details?id=ca.richlink.iment",
  macos:       "https://vennchat.s3.ap-southeast-1.amazonaws.com/vennchat-desktop-mac-universal-7.43.22.dmg",
  windows:     "https://vennchat.s3.ap-southeast-1.amazonaws.com/vennchat-desktop-win-x64-7.43.24.exe",
  androidChn:  "https://vennchat.s3.ap-southeast-1.amazonaws.com/vennchat-app-playChina-prod-release-6.4.12.apk",
};

// ── SVG connection lines (1920 × 1800 coordinate space) ────────
const GCX = 912;
const GCY = 684;
const LINE_TARGETS = [
  { x: 477,  y: 414 },
  { x: 1324, y: 366 },
  { x: 904,  y: 390 },
  { x: 1474, y: 645 },
  { x: 511,  y: 948 },
  { x: 837,  y: 928 },
  { x: 412,  y: 645 },
  { x: 1437, y: 907 },
];

interface HomeProps {
  locale?: Locale;
}

export default function Home({ locale = "en" }: HomeProps) {
  const t = translations[locale];

  const platform = useMemo(() => detectPlatform(), []);

  const ALL_NAV_BTNS = [
    { key: "appStore",   platform: "ios",     label: t.nav.appStore,    icon: ICON_APPLE,       href: DOWNLOAD_URLS.appStore   },
    { key: "iosChn",     platform: "ios",     label: t.nav.iosChn,      icon: ICON_APPLE,       href: DOWNLOAD_URLS.iosChn     },
    { key: "googlePlay", platform: "android", label: t.nav.googlePlay,  icon: ICON_GOOGLE_PLAY, href: DOWNLOAD_URLS.googlePlay },
    { key: "macos",      platform: "macos",   label: t.nav.macos,       icon: ICON_MACOS,       href: DOWNLOAD_URLS.macos      },
    { key: "windows",    platform: "windows", label: t.nav.windows,     icon: ICON_WINDOWS,     href: DOWNLOAD_URLS.windows    },
    { key: "androidChn", platform: "android", label: t.nav.androidChn,  icon: ICON_ANDROID,     href: DOWNLOAD_URLS.androidChn },
  ];

  const NAV_BTNS = platform === "unknown"
    ? ALL_NAV_BTNS
    : ALL_NAV_BTNS.filter((b) => b.platform === platform);

  return (
    <div className="hm">

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 1 · Hero
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-hero">
        <div className="hm-hero-canvas">

          {/* Logo */}
          <img src={LOGO_IMG} alt="VennChat" className="hm-logo-img" />
          <span className="hm-logo-text">VennChat</span>

          {/* Language switcher */}
          <LangSwitcher locale={locale} />

          {/* Small download buttons with platform icons */}
          <div className="hm-nav-btns">
            {NAV_BTNS.map(({ key, label, icon, href }) => (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="hm-nav-btn">
                <img src={icon} alt={label} className="hm-nav-btn-icon" />
                <span className="hm-nav-btn-label">{label}</span>
              </a>
            ))}
          </div>

          {/* Connection lines SVG overlay */}
          <svg
            className="hm-lines-svg"
            viewBox="0 0 1920 1800"
            xmlns="http://www.w3.org/2000/svg"
          >
            {LINE_TARGETS.map((t, i) => (
              <line
                key={i}
                x1={GCX} y1={GCY}
                x2={t.x} y2={t.y}
                stroke="#DDDDDD"
                strokeWidth="1.5"
              />
            ))}
          </svg>

          {/* Globe */}
          <img src={GLOBE_IMG} alt="Globe" className="hm-globe" />

          {/* Love / heart icon */}
          <img src="/new/love.png" alt="" className="hm-love" />

          {/* Floating blobs */}
          <img src={BLOB_SPEECH}   alt="" className="hm-blob hm-blob-speech" />
          <img src={BLOB_TEAL}     alt="" className="hm-blob hm-blob-teal" />
          <img src={BLOB_WHATSAPP} alt="" className="hm-blob hm-blob-whatsapp" />
          <img src={BLOB_CHAT}     alt="" className="hm-blob hm-blob-chat" />
          <img src={BLOB_PERSON}   alt="" className="hm-blob hm-blob-person" />
          <img src={BLOB_BOOKMARK} alt="" className="hm-blob hm-blob-bookmark" />

          {/* Profile photos */}
          <img src={PROFILE_WOMAN} alt="User" className="hm-profile hm-profile-woman" />
          <img src={PROFILE_MAN}   alt="User" className="hm-profile hm-profile-man" />

          {/* Hero text */}
          <h1 className="hm-hero-title">{t.hero.title}</h1>
          <p className="hm-hero-subtitle">
            {t.hero.subtitle.map((line, i) => (
              <React.Fragment key={i}>
                {line}{i < t.hero.subtitle.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
          <p className="hm-hero-desc">{t.hero.desc}</p>

        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 2 · Download
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-download">
        <h2 className="hm-download-title">{t.download.title}</h2>
        <div className="hm-download-grid">
          {(platform === "unknown" || platform === "ios") && (
            <a href={DOWNLOAD_URLS.appStore} target="_blank" rel="noopener noreferrer">
              <img src={DL_APP_STORE} alt={t.nav.appStore} className="hm-dl-card" />
            </a>
          )}
          {(platform === "unknown" || platform === "ios") && (
            <a href={DOWNLOAD_URLS.iosChn} target="_blank" rel="noopener noreferrer">
              <img src={DL_IOS_CHN} alt={t.nav.iosChn} className="hm-dl-card" />
            </a>
          )}
          {(platform === "unknown" || platform === "android") && (
            <a href={DOWNLOAD_URLS.googlePlay} target="_blank" rel="noopener noreferrer">
              <img src={DL_GOOGLE_PLAY} alt={t.nav.googlePlay} className="hm-dl-card" />
            </a>
          )}
          {(platform === "unknown" || platform === "macos") && (
            <a href={DOWNLOAD_URLS.macos} target="_blank" rel="noopener noreferrer">
              <img src={DL_MACOS} alt={t.nav.macos} className="hm-dl-card" />
            </a>
          )}
          {(platform === "unknown" || platform === "windows") && (
            <a href={DOWNLOAD_URLS.windows} target="_blank" rel="noopener noreferrer">
              <img src={DL_WINDOWS} alt={t.nav.windows} className="hm-dl-card" />
            </a>
          )}
          {(platform === "unknown" || platform === "android") && (
            <div className="hm-dl-android-chn">
              <a href={DOWNLOAD_URLS.androidChn} target="_blank" rel="noopener noreferrer">
                <img src={DL_ANDROID_CHN} alt={t.nav.androidChn} className="hm-dl-card" />
              </a>
              <a href="/notification-instructions" className="hm-notif-link">{t.download.notifInstructions}</a>
            </div>
          )}
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 3 · Features header
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-features-hdr">
        <h2 className="hm-features-hdr-title">
          {t.featuresHdr.title.split("\n").map((line, i, arr) => (
            <React.Fragment key={i}>
              {line}{i < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 4 · Chats
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-chats">

        <div className="hm-chats-phones hm-chats-phones-left">
          <img src={GROUPS_S1} alt="" className="hm-phone hm-phone-a" />
          <img src={GROUPS_S2} alt="" className="hm-phone hm-phone-b" />
          <img src={CHAT_S1}   alt="" className="hm-phone hm-phone-c" />
        </div>

        <div className="hm-chats-text">
          <div className="hm-feat-banner hm-feat-banner-chats">
            <span className="hm-feat-label hm-feat-label-chats">{t.chats.bannerLabel}</span>
            <img src={BLOB_TEAL_FEATURE} alt="" className="hm-banner-blob-right" />
          </div>
          <p className="hm-feat-subtitle">{t.chats.subtitle}</p>
          <h3 className="hm-feat-name">{t.chats.name}</h3>
          <img src={QUOT_CHATS} alt="" className="hm-quot hm-quot-open" />
          <p className="hm-feat-desc">{t.chats.desc}</p>
          <img src={QUOT_CHATS} alt="" className="hm-quot hm-quot-close hm-quot-faint" />
        </div>

        <div className="hm-chats-phones hm-chats-phones-right">
          <img src={CHAT_S2} alt="" className="hm-phone hm-phone-d" />
          <img src={CHAT_S3} alt="" className="hm-phone hm-phone-e" />
          <img src={CHAT_S4} alt="" className="hm-phone hm-phone-f" />
        </div>

      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 5 · Discovery
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-discovery">

        <div className="hm-discovery-text">
          <div className="hm-feat-banner hm-feat-banner-discovery">
            <img src={BLOB_TEAL2} alt="" className="hm-discovery-blob" />
            <span className="hm-feat-label hm-feat-label-discovery">{t.discovery.bannerLabel}</span>
          </div>
          <p className="hm-feat-subtitle">{t.discovery.subtitle}</p>
          <h3 className="hm-feat-name">{t.discovery.name}</h3>
          <img src={QUOT_DISCOVERY} alt="" className="hm-quot hm-quot-open" />
          <p className="hm-feat-desc">{t.discovery.desc}</p>
          <img src={QUOT_DISCOVERY} alt="" className="hm-quot hm-quot-close hm-quot-faint" />
        </div>

        <div className="hm-discovery-visual">
          <img src={DISCOVERY_BG}    alt="" className="hm-discovery-bg" />
          <img src={DISCOVERY_PHONE} alt="Discovery" className="hm-discovery-phone" />
          <p className="hm-discovery-post-text">
            {t.discovery.postText.map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}{i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        </div>

      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 6 · Groups
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-groups">

        <div className="hm-groups-visual">
          <img src={SECURITY_IMG} alt="Community" className="hm-groups-img" />
        </div>

        <div className="hm-groups-text">
          <p className="hm-groups-discover">{t.groups.discover}</p>
          <div className="hm-feat-banner hm-feat-banner-groups">
            <span className="hm-feat-label hm-feat-label-groups">{t.groups.bannerLabel}</span>
            <img src={BLOB_GROUPS} alt="" className="hm-banner-blob-right" />
          </div>
          <h3 className="hm-feat-name">{t.groups.name}</h3>
          <img src={QUOT_GROUPS} alt="" className="hm-quot hm-quot-open" />
          <p className="hm-feat-desc">{t.groups.desc}</p>
          <img src={QUOT_GROUPS} alt="" className="hm-quot hm-quot-close hm-quot-faint" />
        </div>

      </section>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
          Section 7 · User Security / Profile
      ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="hm-security">

        <div className="hm-security-text">
          <div className="hm-feat-banner hm-feat-banner-security">
            <img src={BLOB_SECURITY} alt="" className="hm-security-blob" />
            <span className="hm-feat-label hm-feat-label-security">{t.security.bannerLabel}</span>
          </div>
          <p className="hm-feat-subtitle">{t.security.subtitle}</p>
          <h3 className="hm-feat-name">{t.security.name}</h3>
          <img src={QUOT_SECURITY} alt="" className="hm-quot hm-quot-open" />
          <p className="hm-feat-desc">{t.security.desc}</p>
          <img src={QUOT_SECURITY} alt="" className="hm-quot hm-quot-close hm-quot-faint" />
        </div>

        <div className="hm-security-visual">
          <img src={IPHONE_MOCKUP} alt="iPhone mockup" className="hm-security-phone" />
        </div>

      </section>

    </div>
  );
}
